// Typed wrappers for the ADMIN/SUPPORT refund panel (issue #189, WAVE D2).
// All five paths exist in openapi.yaml, so we drive them through the shared
// apiClient (cookie session + CSRF + 401-retry middleware) and never touch
// raw fetch. The raw `token`/`url` on create+regenerate is returned once.
import { apiClient } from "@/lib/api/client";
import { RefundRequestHttpError } from "@/features/refunds/api";
import type { components, operations } from "@generated/api/schema";

export type RefundList = components["schemas"]["RefundListResponse"];
export type RefundDetail = components["schemas"]["RefundRequestDetailResponse"];
export type RefundEvent = components["schemas"]["RefundRequestEventResponse"];
export type RefundStatus = RefundDetail["status"];
export type CreateRefundLinkBody =
  components["schemas"]["CreateRefundLinkRequest"];
export type CreateRefundLinkResult =
  components["schemas"]["CreateRefundLinkResponse"];
export type TransitionRefundBody =
  components["schemas"]["TransitionRefundRequest"];
export type RegenerateLinkResult =
  components["schemas"]["RegenerateLinkResponse"];

export type ListRefundsParams = NonNullable<
  operations["list_refund_requests_api_v2_admin_refund_requests_get"]["parameters"]["query"]
>;

/** Page of refund requests, newest-first, optionally filtered by status/search. */
export async function fetchRefundRequests(
  params: ListRefundsParams = {},
): Promise<RefundList> {
  const { data, error } = await apiClient.GET("/api/v2/admin/refund-requests", {
    params: { query: params },
  });
  if (error || !data)
    throw new Error(`fetchRefundRequests failed: ${JSON.stringify(error)}`);
  return data;
}

/**
 * One refund request with its full event timeline. Throws a status-carrying
 * RefundRequestHttpError so the detail page can tell a real 404 ("não
 * encontrada") apart from network/500 failures (audit item 3).
 */
export async function fetchRefundRequest(
  requestId: string,
): Promise<RefundDetail> {
  const { data, error, response } = await apiClient.GET(
    "/api/v2/admin/refund-requests/{request_id}",
    { params: { path: { request_id: requestId } } },
  );
  if (error || !data)
    throw new RefundRequestHttpError(
      response.status,
      `fetchRefundRequest id=${requestId}`,
      error,
    );
  return data;
}

/** Mint a refund link; the raw token+url come back exactly once. */
export async function createRefundLink(
  body: CreateRefundLinkBody,
): Promise<CreateRefundLinkResult> {
  const { data, error } = await apiClient.POST(
    "/api/v2/admin/refund-requests",
    { body },
  );
  if (error || !data)
    throw new Error(`createRefundLink failed: ${JSON.stringify(error)}`);
  return data;
}

/** Advance a request through the status machine (server-validated). */
export async function transitionRefundRequest(
  requestId: string,
  body: TransitionRefundBody,
): Promise<RefundDetail> {
  const { data, error } = await apiClient.POST(
    "/api/v2/admin/refund-requests/{request_id}/transition",
    { params: { path: { request_id: requestId } }, body },
  );
  if (error || !data)
    throw new Error(
      `transitionRefundRequest failed id=${requestId}: ${JSON.stringify(error)}`,
    );
  return data;
}

/** Rotate the token (expiring the prior one); fresh raw token returned once. */
export async function regenerateRefundLink(
  requestId: string,
): Promise<RegenerateLinkResult> {
  const { data, error } = await apiClient.POST(
    "/api/v2/admin/refund-requests/{request_id}/regenerate-link",
    { params: { path: { request_id: requestId } } },
  );
  if (error || !data)
    throw new Error(
      `regenerateRefundLink failed id=${requestId}: ${JSON.stringify(error)}`,
    );
  return data;
}
